// src/pages/FinancialsPage.tsx
//
// Financials — QuickBooks Desktop-sourced P&L / business-ops module.
// Layout mirrors AnalyticsHubPage: header with back button, left sidebar
// on desktop, horizontal pill strip on mobile, both rendered from the same
// SECTIONS array. The whole page is gated by the single `financials`
// feature (tile visibility on LandingPage); sections with a non-null
// `feature` are additionally gated per tenant/user the same way the
// Analytics hub does it (adminApi feature data + isFeatureEnabled).
// Sections with feature:null are always visible once you're on the page.
//
// Data comes from the agent's QBD module: recipes (qbd-pnl-summary,
// qbd-pnl, qbd-balance-sheet, qbd-expense-breakdown, qbd-ar-aging,
// qbd-ap-aging, qbd-budget-vs-actual) plus the /api/qbd/status, /api/qbd/sync,
// /api/qbd/adjustments, and /api/qbd/budget direct endpoints.
// See src/services/apiV2.ts.

import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Landmark, LayoutDashboard, FileSpreadsheet, Receipt,
  CreditCard, Users, Clock, Plug, Scale, Target,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { adminApi } from '../services/adminApi';
import { isFeatureEnabled } from '../lib/featureCatalog';
import { hasAdminRole } from '../lib/sysadminRoles';
import { useSection } from '../lib/sectionStore';
import { OverviewTab } from './financials/OverviewTab';
import { PnLTab } from './financials/PnLTab';
import { BalanceSheetTab } from './financials/BalanceSheetTab';
import { ArAgingTab } from './financials/ArAgingTab';
import { ApAgingTab } from './financials/ApAgingTab';
import { BudgetTab } from './financials/BudgetTab';
import { PayrollTab } from './financials/PayrollTab';
import { TimeCardsTab } from './financials/TimeCardsTab';
import { ConnectionTab } from './financials/ConnectionTab';
import { QboStatementTab } from './financials/QboStatementTab';
import { useModuleAccess } from '../hooks/useModuleAccess';

type Section = 'overview' | 'pnl' | 'balancesheet' | 'ar' | 'ap' | 'budget' | 'payroll' | 'timecards' | 'connection';

interface SectionDef {
  key: Section;
  feature: string | null;   // null = always visible once the page is reachable
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  qbo?: boolean;            // also shown on QuickBooks Online tenants (financials.qbo)
}

const SECTIONS: SectionDef[] = [
  { key: 'overview',     feature: null, label: 'Overview',      icon: LayoutDashboard },
  { key: 'pnl',          feature: null, label: 'Profit & Loss', icon: FileSpreadsheet, qbo: true },
  { key: 'balancesheet', feature: 'financials.balancesheet', label: 'Balance Sheet', icon: Scale, qbo: true },
  { key: 'ar',           feature: null, label: 'A/R Aging',     icon: Receipt },
  { key: 'ap',           feature: null, label: 'A/P Aging',     icon: CreditCard },
  { key: 'budget',       feature: 'financials.budget', label: 'Budget', icon: Target },
  { key: 'payroll',      feature: 'financials.payroll', label: 'Payroll',    icon: Users },
  { key: 'timecards',    feature: 'financials.payroll', label: 'Time Cards', icon: Clock },
  { key: 'connection',   feature: null, label: 'Connection',    icon: Plug },
];

interface FinancialsPageProps {
  onBack?: () => void;
}

export default function FinancialsPage({ onBack }: FinancialsPageProps) {
  const { user, branding, claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;
  const userRoles: string[] = claims?.roles || [];
  const showAll = userRoles.length === 0;      // dev bypass (no roles = local dev)
  const isSysadmin = hasAdminRole(userRoles);

  // Feature data — same resolution the Analytics hub uses for its sections.
  const featuresQ = useQuery({
    queryKey: ['financials', 'features', slug],
    queryFn: () => adminApi.getAllFeatures(slug ?? undefined),
    enabled: !!slug && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const effectiveQ = useQuery({
    queryKey: ['financials', 'effective', slug, user?.id],
    queryFn: () => adminApi.getEffectiveFeatures(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const overridesQ = useQuery({
    queryKey: ['financials', 'overrides', slug, user?.id],
    queryFn: () => adminApi.getAllUserOverrides(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id && !showAll && !isSysadmin,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });

  const effectiveSet  = new Set(effectiveQ.data ?? []);
  const dbFeatureKeys = new Set((featuresQ.data ?? []).map((f) => f.featureKey));
  const userOverrides = new Map<string, boolean>(
    (overridesQ.data ?? []).map((o) => [o.featureKey, o.isEnabled]),
  );
  const featuresLoading = featuresQ.isLoading || effectiveQ.isLoading || overridesQ.isLoading;
  const featuresErrored = !!(featuresQ.error || effectiveQ.error || overridesQ.error);

  function canSeeSection(s: SectionDef): boolean {
    if (s.feature === null) return true;
    if (showAll || isSysadmin) return true;
    if (featuresLoading) return false;
    if (featuresErrored) {
      return isFeatureEnabled(s.feature, new Set(), new Set(), new Map());
    }
    return isFeatureEnabled(s.feature, effectiveSet, dbFeatureKeys, userOverrides);
  }

  // financials.qbo: the tenant keeps its books in QuickBooks Online, so the
  // statements come from QBO's own reports and the QuickBooks Desktop-only
  // sections (overview, aging, budget, payroll, connection) are hidden.
  const { tenantHas } = useModuleAccess();
  const qboMode = tenantHas('financials.qbo');
  const visibleSections = SECTIONS.filter((s) => canSeeSection(s) && (!qboMode || s.qbo));
  const [storedSection, setStoredSection] = useSection('financials', qboMode ? 'pnl' : 'overview');
  const activeSection = storedSection as Section;
  const setActiveSection = (s: Section) => setStoredSection(s);

  // If the active section becomes hidden (e.g. admin revoked it mid-session),
  // fall back to whatever's first in the visible list.
  const safeActive: Section =
    visibleSections.some((s) => s.key === activeSection)
      ? activeSection
      : (visibleSections[0]?.key ?? (qboMode ? 'pnl' : 'overview'));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1900px] mx-auto">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                  title="Back to home"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
                  <Landmark size={18} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white tracking-tight">Financials</h1>
                  <p className="text-xs text-slate-500">
                    {branding?.company_name || 'GeniReports'} · {qboMode ? 'QuickBooks Online' : 'QuickBooks Desktop'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile: horizontal scrollable pill strip (hidden at md+ where the
            sidebar takes over). Same SECTIONS array as the sidebar. */}
        <nav className="md:hidden border-b border-slate-800/50 px-4 py-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ul className="flex items-center gap-1.5 w-max">
            {visibleSections.map((s) => {
              const Icon = s.icon;
              const isActive = safeActive === s.key;
              return (
                <li key={s.key} className="shrink-0">
                  <button
                    onClick={() => setActiveSection(s.key)}
                    className={`flex items-center gap-1.5 px-3 py-2.5 rounded-md text-sm whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-indigo-500/15 text-indigo-200 border border-indigo-500/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                    }`}
                  >
                    <Icon size={15} />
                    <span>{s.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex">
          {/* Main panel — top-level section nav is the global app sidebar now. */}
          <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
            {safeActive === 'overview'     && <OverviewTab />}
            {safeActive === 'pnl'          && (qboMode ? <QboStatementTab kind="pnl" /> : <PnLTab />)}
            {safeActive === 'balancesheet' && (qboMode ? <QboStatementTab kind="balanceSheet" /> : <BalanceSheetTab />)}
            {safeActive === 'ar'           && <ArAgingTab />}
            {safeActive === 'ap'           && <ApAgingTab />}
            {safeActive === 'budget'       && <BudgetTab />}
            {safeActive === 'payroll'    && <PayrollTab />}
            {safeActive === 'timecards'  && <TimeCardsTab />}
            {safeActive === 'connection' && <ConnectionTab />}
          </main>
        </div>
      </div>
    </div>
  );
}
